import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import hubspotSubmit from 'hubspot-form-submit';

import Align from '@marvelapp/marvel-ui/align';
import Button from '@marvelapp/marvel-ui/button';
import MarvelLink from '@marvelapp/marvel-ui/link';
import MaxWidth from '@marvelapp/marvel-ui/max-width';
import MessageBlock from '@marvelapp/marvel-ui/message-block';
import Paragraph from '@marvelapp/marvel-ui/paragraph';
import Spacing from '@marvelapp/marvel-ui/spacing';

import UnderlinedInputField from 'marketing-website/utils/forms/UnderlinedInputField';
import { splitNames } from 'marketing-website/utils/forms';
import {
  isRequired,
  isEmailValid,
  isPhoneNumberValid,
  minLength,
} from 'marketing-website/utils/forms/validation';

import { HUBSPOT_ID } from 'marketing-website/constants/api';
import { navigateTo } from 'gatsby-link';

import Pricing from 'marketing-website/components/icons/Pricing/index.js';

const fields = [
  {
    fieldOptions: {
      name: 'name',
      placeholder: 'Full Name',
      label: 'Full Name',
    },
    validate: isRequired,
  },
  {
    fieldOptions: {
      name: 'email',
      placeholder: 'Email Address',
      type: 'email',
      label: 'Work Email Address',
    },
    validate: [isRequired, isEmailValid],
  },
  {
    fieldOptions: {
      name: 'phoneNumber',
      placeholder: 'Phone Number',
      type: 'tel',
      label: 'Phone Number',
    },
    validate: [isRequired, isPhoneNumberValid, minLength(3)],
  },
  {
    fieldOptions: {
      name: 'jobTitle',
      placeholder: 'Job Title',
      label: 'Job Title',
    },
    validate: [isRequired],
  },
  {
    fieldOptions: {
      name: 'companyName',
      placeholder: 'Company Name',
      label: 'Company Name',
    },
    validate: [isRequired],
  },
];

class WebinarForm extends React.Component {
  state = {
    formState: 'initial',
  };

  submitForm = ({ name, email, phoneNumber, jobTitle, companyName }) => {
    const names = splitNames(name);
    const firstName = names.firstName;
    const lastName = names.lastName;

    hubspotSubmit(HUBSPOT_ID, this.props.hubspotId, {
      company: companyName,
      email,
      firstname: firstName,
      lastname: lastName,
      phone: phoneNumber,
      jobtitle: jobTitle,
    })
      .then(() => {
        this.setState({
          formState: 'success',
        });
      })
      .catch(() => {
        this.setState({
          formState: 'error',
        });
      });
  };

  render() {
    const {
      handleSubmit,
      ctaCopy,
      btnColor,
      headerTitle,
      headerText,
      infoMessage,
      url,
      disallowedFields,
    } = this.props;
    const { formState } = this.state;
    const successState = formState === 'success';
    const allowedFields = [];
    for (let i = 0; i < fields.length; i += 1) {
      let allowedField = fields[i];
      for (let j = 0; j < disallowedFields.length; j += 1) {
        const disallowedField = disallowedFields[j];
        if (allowedField.fieldOptions.name === disallowedField) {
          allowedField = null;
          break;
        }
      }
      if (allowedField) allowedFields.push(allowedField);
    }

    return (
      <div>
        {headerTitle}
        {headerText}

        <form
          onSubmit={handleSubmit(this.submitForm)}
          className="display-block"
          noValidate
          method="post"
        >
          {formState === 'error' && (
            <MessageBlock type="error">
              There was an error submitting this form. Please try again or{' '}
              <MarvelLink
                url="https://help.marvelapp.com/"
                type="opaqueReversed"
                hasUnderline
                target="_blank"
              >
                contact support.
              </MarvelLink>
            </MessageBlock>
          )}
          {allowedFields.map(field => (
            <Spacing
              key={field.fieldOptions.name}
              type="margin"
              size="m"
              position="bottom"
            >
              <Field
                {...field.fieldOptions}
                component={field.component || UnderlinedInputField}
                fullWidth
                validate={field.validate}
                popoverPosition="left"
                popoverWidth={400}
              />
            </Spacing>
          ))}
          <Button
            type="submit"
            shape="rounded"
            fullWidth
            size="large"
            background={successState ? 'green' : btnColor}
            hover={successState ? 'green' : btnColor}
            iconColor="white"
            hasSucceeded={successState}
            isStateful
          >
            {ctaCopy}
          </Button>
          <Align type="center">
            <MaxWidth value={300}>
              <Spacing type="margin" size="m" position="topBottom">
                <Spacing type="padding" size="m" position="leftRight">
                  {successState ? (
                    navigateTo(url)
                  ) : (
                    <span
                      style={{
                        display: 'flex',
                      }}
                    >
                      <div style={{ transform: 'translate(-10px, 0)' }}>
                        <Pricing />
                      </div>
                      <Paragraph size="l">{infoMessage}</Paragraph>
                    </span>
                  )}
                </Spacing>
              </Spacing>
            </MaxWidth>
          </Align>
        </form>
      </div>
    );
  }
}

WebinarForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  ctaCopy: PropTypes.string,
  btnColor: PropTypes.string,
  headerTitle: PropTypes.string,
  headerText: PropTypes.string,
  hubspotId: PropTypes.string.isRequired,
  infoMessage: PropTypes.string,
  url: PropTypes.string.isRequired,
  disallowedFields: PropTypes.array,
};

WebinarForm.defaultProps = {
  btnColor: 'blue',
  ctaCopy: 'Request a trial',
  infoMessage: 'Access the Webinar',
  url: '/resources/webinar-sketch-recording-10-2018',
  disallowedFields: [],
};

export default reduxForm({
  form: 'webinarForm',
  touchOnBlur: false,
})(WebinarForm);



// WEBPACK FOOTER //
// ./src/components/modules/WebinarForm/index.js